import styled from '@emotion/styled';
import {
  Badge,
  Body,
  Box,
  Container,
  ContainerProps,
  Heading,
  Icon,
} from '@hover/blueprint';
import { iArrowUp } from '@hover/icons';
import GatsbyImage from 'gatsby-image';
import React, { FC } from 'react';
import { BLOG_CATEGORIES } from '../../../constants';
import { useBackToTop } from '../../../hooks';
import { IBlogPost } from '../../../interfaces/IBlogPost';
import { BackToTopButton } from '../../common';
import BlogPostParser from './BlogPostParser';
import BlogPostShareButtons from './BlogPostShareButtons';

const StyledImage = styled(GatsbyImage)`
  div:first-child {
    padding-bottom: 50% !important;
  }
`;

interface Props {
  post: IBlogPost;
  pt?: ContainerProps['pt'];
  pb?: ContainerProps['pb'];
}

const BlogPost: FC<Props> = ({ post, pb, pt }) => {
  const postCategoryData = BLOG_CATEGORIES.find(
    item => item.value === post.categories[0],
  );
  const { showButton, backToTop } = useBackToTop();
  return (
    <Container
      maxW="1248px"
      pb={pb}
      pt={pt}
      overflow="visible"
      pl={500}
      pr={{ base: 500, desktop: 0 }}
    >
      <Box display="flex" flexDirection={{ base: 'column', tablet: 'row' }}>
        <Box
          display="flex"
          flexDirection="column"
          width={{ base: '100%', tablet: '28%' }}
        >
          {postCategoryData && (
            <Box pb={{ base: 500, tablet: 800 }}>
              <Badge
                colorScheme={postCategoryData.color}
                variant="brand"
                textTransform="uppercase"
              >
                {postCategoryData.value}
              </Badge>
            </Box>
          )}
          {post?.author && (
            <Box pb={{ base: 400, tablet: 500 }}>
              <Body size={{ base: 300, table: 500 }} fontStyle="italic">
                {post.author}
              </Body>
            </Box>
          )}
          <Body pb={{ base: 500, tablet: '80px' }} size={300}>
            {post.publishDate}
          </Body>
          <Box
            position="sticky"
            top={{ tablet: '144px', desktop: '176px' }}
            display={{ base: 'none', tablet: 'flex' }}
            flexDir="column"
            alignItems="flex-start"
          >
            <BlogPostShareButtons />
            {showButton && (
              <Box
                mt={700}
                mb={600}
                cursor="pointer"
                onClick={backToTop}
                alignItems="center"
              >
                <Body color="primary.600" isSemiBold>
                  Back to top
                </Body>
                <Icon
                  ml={200}
                  strokeWidth="2px"
                  size="default"
                  icon={iArrowUp}
                  color="primary.600"
                />
              </Box>
            )}
          </Box>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          width={{ base: '100%', tablet: '75%' }}
        >
          <Box display="flex" flexDirection="column">
            <Heading
              as="h2"
              size={{ base: 500, tablet: 700 }}
              width={{ base: '100%', tablet: '80%' }}
            >
              {post.title}
            </Heading>
            <Box
              width={{ base: '100%', tablet: '80%' }}
              mb={{ base: 400, tablet: 700 }}
            >
              <StyledImage
                loading="lazy"
                alt={post.title}
                style={{
                  width: '100%',
                  backgroundSize: 'cover',
                  background: '#161e2f no-repeat center center',
                }}
                fluid={post.heroImage.fluid}
              />
            </Box>
            <BlogPostParser html={post.body.childMarkdownRemark.html} />
          </Box>
        </Box>
        <Box
          mt={{ base: 800, tablet: 0 }}
          display={{ base: 'flex', tablet: 'none' }}
          w="100%"
          flexDir="column"
          alignItems="center"
        >
          <BlogPostShareButtons />
        </Box>
      </Box>
      <BackToTopButton />
    </Container>
  );
};

BlogPost.defaultProps = {
  pb: 0,
  pt: 0,
};

export default BlogPost;
